// NodePhoneField.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatPhoneValueForInput, isPhoneMaskableInput, normalizePhoneValueForStorage } from 'utilities/Phone';

// CSS
import './NodePhoneField.css';

// Components
import TextInput from 'components/textinput/TextInput';

/**
 * NodePhoneField
 *
 * Displays a dedicated US-first phone input with lenient masking and canonical save behavior.
 * In readOnly / previewMode (visitor Peek): display-only formatted value.
 *
 * @param {string} value - Current stored field value.
 * @param {Function} onChange - Callback when a new phone value is saved.
 * @param {boolean} [readOnly=false] - Display-only.
 * @param {boolean} [previewMode=false] - Alias for readOnly (NodeField visitor path).
 *
 * @returns {ReactNode} NodePhoneField.
 */
const NodePhoneField = ({
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const isReadOnly = Boolean(readOnly || previewMode);

  // ----- STATE ----- //

  const [draftValue, setDraftValue] = useState(formatPhoneValueForInput(value));

  // ----- EFFECTS ----- //

  useEffect(() => {
    setDraftValue(formatPhoneValueForInput(value));
  }, [value]);

  // ----- HANDLERS ----- //

  const handleChange = useCallback((event) => {
    if (isReadOnly) return;
    const nextValue = event?.target?.value ?? '';

    if (!isPhoneMaskableInput(nextValue)) {
      setDraftValue(nextValue);
      return;
    }

    setDraftValue(formatPhoneValueForInput(nextValue));
  }, [isReadOnly]);

  const handleBlur = useCallback(() => {
    if (isReadOnly || typeof onChange !== 'function') return;

    const nextValue = normalizePhoneValueForStorage(draftValue);
    const currentValue = value == null ? '' : String(value);

    if (nextValue !== currentValue) {
      onChange(nextValue);
      return;
    }

    setDraftValue(formatPhoneValueForInput(currentValue));
  }, [draftValue, isReadOnly, onChange, value]);

  // ----- RENDER ----- //

  return (
    <>
      <div className="node-phone-field-container">

        <TextInput
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={draftValue}
          placeholder={isReadOnly ? '' : '(415) 555-1212'}
          startIconKey="call"
          startIconColor={theme.foregroundMuted}
          iconSize={22}
          hybrid
          readOnly={isReadOnly}
          onBlur={handleBlur}
          onChange={isReadOnly ? undefined : handleChange}
        />
      </div>
    </>
  );
};

NodePhoneField.displayName = 'NodePhoneField';

export default NodePhoneField;
